import { Injectable, OnDestroy } from '@angular/core';
import { Client, IMessage } from '@stomp/stompjs';
import { BehaviorSubject } from 'rxjs';
import SockJS from 'sockjs-client';

import {
  IConnection,
  IConnectionStatus,
  IStoredSubscription,
  IWebSocketConfig,
  IWebSocketMessage,
} from './web-socket.interface';

@Injectable({
  providedIn: 'root',
})
export class WebSocketService implements OnDestroy {
  private _connections: Map<string, IConnection> = new Map();
  private readonly _reconnectInterval = 5000;

  /**
   * Clean up all connections on destroy
   */
  public ngOnDestroy(): void {
    this.disconnectAll();
  }

  /**
   * Establishes a WebSocket STOMP connection over SockJS. When called again
   * for an existing (disconnected) connection it reuses the connection
   * state, so consumers keep their connectionStatus$ reference and all
   * registered subscriptions are re-established on the new client.
   *
   * Reconnection is handled natively by the STOMP client (reconnectDelay),
   * which also replays subscriptions via onConnect on every reconnect.
   */
  public connect(
    connectionId: string,
    config: IWebSocketConfig
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const existingConnection = this._connections.get(connectionId);

      if (existingConnection?.client?.connected) {
        console.warn(`WebSocket connection '${connectionId}' already exists.`);
        resolve();
        return;
      }

      if (existingConnection?.connectionStatus$.value.connecting) {
        console.warn(
          `WebSocket connection '${connectionId}' is already connecting.`
        );
        resolve();
        return;
      }

      const currentUserStr = localStorage.getItem('currentUser');
      const currentUser = currentUserStr ? JSON.parse(currentUserStr) : null;
      const token = currentUser?.idToken;

      const client = new Client({
        webSocketFactory: () => new SockJS(config.url),
        reconnectDelay: config.reconnectDelay ?? this._reconnectInterval,
        heartbeatIncoming: config.heartbeatIncoming ?? 0,
        heartbeatOutgoing: config.heartbeatOutgoing ?? 0,
        connectHeaders: {
          ...(config.headers || {}),
          Authorization: token ? `Bearer ${token}` : '',
        },
        debug: config.debug
          ? (message: string) => console.log(message)
          : () => undefined,
      });

      const connection: IConnection =
        existingConnection ??
        ({
          config,
          subscriptions: new Map(),
          connectionStatus$: new BehaviorSubject<IConnectionStatus>({
            connected: false,
            connecting: true,
          }),
        } as IConnection);

      connection.client = client;
      connection.config = config;
      connection.connectionStatus$.next({ connected: false, connecting: true });

      // Register the connection BEFORE activating so consumers and native
      // reconnect callbacks can find it even before the first connect.
      this._connections.set(connectionId, connection);

      let resolved = false;

      client.onConnect = () => {
        this._restoreSubscriptions(connection);
        connection.connectionStatus$.next({
          connected: true,
          connecting: false,
        });
        if (!resolved) {
          resolved = true;
          resolve();
        }
      };

      client.onStompError = (frame) => {
        const errorMessage = frame.headers?.['message'] || 'STOMP error';
        console.error(
          `WebSocket STOMP error for '${connectionId}':`,
          errorMessage,
          frame.body
        );
        connection.connectionStatus$.next({
          connected: false,
          connecting: false,
          error: errorMessage,
        });
        if (!resolved) {
          resolved = true;
          reject(new Error(errorMessage));
        }
      };

      client.onWebSocketError = () => {
        connection.connectionStatus$.next({
          connected: false,
          connecting: false,
        });
      };

      client.onWebSocketClose = () => {
        connection.connectionStatus$.next({
          connected: false,
          connecting: false,
        });
      };

      client.activate();
    });
  }

  /**
   * Disconnects a specific connection by ID
   */
  public disconnect(connectionId: string): void {
    const connection = this._connections.get(connectionId);
    if (!connection) {
      return;
    }

    connection.subscriptions.forEach((sub) => sub.subscription?.unsubscribe());
    connection.subscriptions.clear();

    connection.client.deactivate();

    connection.connectionStatus$.next({ connected: false, connecting: false });
    connection.connectionStatus$.complete();

    this._connections.delete(connectionId);
  }

  /**
   * Disconnects all active connections
   */
  public disconnectAll(): void {
    this._connections.forEach((_, id) => this.disconnect(id));
  }

  /**
   * Subscribes to a STOMP destination. The subscription is stored durably and
   * automatically re-established after a reconnect.
   * @returns subscriptionId
   */
  public subscribe<T>(
    connectionId: string,
    destination: string,
    callback: (message: IWebSocketMessage<T>) => void,
    headers?: Record<string, string>
  ): string {
    const connection = this._connections.get(connectionId);
    if (!connection?.client?.connected) {
      throw new Error(
        `WebSocket connection '${connectionId}' is not connected.`
      );
    }

    const subscriptionId = this._generateSubscriptionId();
    const stored: IStoredSubscription = {
      id: subscriptionId,
      destination,
      callback: callback as (message: IWebSocketMessage<unknown>) => void,
      headers,
    };

    this._attachStompSubscription(connection, stored);
    connection.subscriptions.set(subscriptionId, stored);

    return subscriptionId;
  }

  /**
   * Unsubscribes from a given subscription ID
   */
  public unsubscribe(connectionId: string, subscriptionId: string): void {
    const connection = this._connections.get(connectionId);
    if (!connection) {
      return;
    }

    const subscription = connection.subscriptions.get(subscriptionId);
    if (subscription) {
      subscription.subscription?.unsubscribe();
      connection.subscriptions.delete(subscriptionId);
    }
  }

  /**
   * Sends a message to a destination
   */
  public send(
    connectionId: string,
    destination: string,
    body: unknown,
    headers?: Record<string, string>
  ): void {
    const connection = this._connections.get(connectionId);
    if (!connection?.client?.connected) {
      throw new Error(
        `WebSocket connection '${connectionId}' is not connected.`
      );
    }

    const messageBody = typeof body === 'string' ? body : JSON.stringify(body);
    connection.client.publish({
      destination,
      body: messageBody,
      headers: headers || {},
    });
  }

  /**
   * Checks if a connection is active
   */
  public isConnected(connectionId: string): boolean {
    return this._connections.get(connectionId)?.client?.connected ?? false;
  }

  /**
   * Parses a raw STOMP message into a typed WebSocket message
   * @param message STOMP message from the STOMP client
   * @returns Typed WebSocket message
   */
  private _parseMessage<T>(message: IMessage): IWebSocketMessage<T> {
    let parsedBody: T;

    try {
      parsedBody = JSON.parse(message.body);
    } catch {
      parsedBody = message.body as unknown as T;
    }

    return {
      body: parsedBody,
      headers: message.headers,
      destination: message.headers?.['destination'] ?? '',
    };
  }

  /**
   * Generates a unique subscription ID
   */
  private _generateSubscriptionId(): string {
    return `sub-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }

  /**
   * Binds a stored subscription to the connection's current STOMP client.
   */
  private _attachStompSubscription(
    connection: IConnection,
    stored: IStoredSubscription
  ): void {
    stored.subscription = connection.client.subscribe(
      stored.destination,
      (message: IMessage) => {
        const parsedMessage = this._parseMessage(message);
        stored.callback(parsedMessage);
      },
      stored.headers
    );
  }

  /**
   * Replays all stored subscriptions on a freshly (re)connected client.
   */
  private _restoreSubscriptions(connection: IConnection): void {
    connection.subscriptions.forEach((stored) =>
      this._attachStompSubscription(connection, stored)
    );
  }
}
